import React from 'react';
import { Sparkles, TrendingUp, Target, Activity, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PREMIUM_PRICING } from '@/config/pricingConfig';
import LaunchOfferBadge from '@/components/LaunchOfferBadge';

const PremiumUpsellScreen = ({
  title,
  subtitle,
  features = [],
  buttonText,
  onUpgrade,
  icon: IconComponent = Sparkles,
  language = 'fr'
}) => {
  const navigate = useNavigate();

  const defaultFeatures = [
    {
      icon: TrendingUp,
      title: language === 'en' ? 'Gentle progress insights' : 'Repères de progression',
      description: language === 'en'
        ? 'Follow your listening rhythm, consistency and evolution over time.'
        : "Suivez votre rythme d'écoute, votre constance et votre évolution dans le temps."
    },
    {
      icon: Activity,
      title: language === 'en' ? 'A clearer listening picture' : 'Une lecture plus claire',
      description: language === 'en'
        ? 'Visualize your listening time and personal trends with calm, readable indicators.'
        : "Visualisez votre temps d'écoute et vos tendances personnelles avec des repères simples et lisibles."
    },
    {
      icon: Brain,
      title: language === 'en' ? 'More personal guidance' : 'Un accompagnement plus personnel',
      description: language === 'en'
        ? 'Receive softer, more contextual suggestions to help you continue at your own pace.'
        : "Recevez des suggestions plus douces et contextuelles pour avancer à votre rythme."
    },
    {
      icon: Target,
      title: language === 'en' ? 'Listening intentions' : 'Intentions d’écoute',
      description: language === 'en'
        ? 'Set simple intentions and let Hearen gently support your routine.'
        : "Posez des intentions simples et laissez Hearen soutenir doucement votre routine."
    }
  ];

  const displayFeatures = features.length > 0 ? features : defaultFeatures;

  const defaultTitle = language === 'en'
    ? 'Go a little further with Hearen'
    : 'Aller un peu plus loin avec Hearen';

  const defaultSubtitle = language === 'en'
    ? 'Hearen Premium gently enriches your listening experience with deeper personalization, clearer progress insights and more attentive guidance.'
    : "Hearen Premium enrichit doucement votre expérience avec une personnalisation plus profonde, des repères de progression plus clairs et un accompagnement plus attentif.";

  const defaultButtonText = language === 'en'
    ? 'Discover Hearen Premium'
    : 'Découvrir Hearen Premium';

  const handlePremium = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/premium');
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1520] pt-24 px-4 pb-40">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/5 border border-white/10 rounded-full mb-5 shadow-[0_0_35px_rgba(120,110,255,0.18)]">
            <IconComponent className="w-8 h-8 text-white/75" />
          </div>

          <h1 className="text-2xl font-semibold text-white mb-3 leading-tight">
            {title || defaultTitle}
          </h1>

          <p className="text-sm text-white/60 max-w-md mx-auto leading-relaxed">
            {subtitle || defaultSubtitle}
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#1B2230]/95 to-[#111722]/95 border border-white/10 rounded-3xl p-6 backdrop-blur-md shadow-[0_0_45px_rgba(120,110,255,0.16)] mb-8">
          <div className="flex items-start gap-4 mb-5">
            <Sparkles className="w-5 h-5 text-white/65 flex-shrink-0 mt-1" />

            <div className="flex-1">
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'en' ? 'Available with Hearen Premium' : 'Disponible avec Hearen Premium'}
              </h3>

              <p className="text-sm text-white/65 leading-relaxed mb-4">
                {language === 'en'
                  ? 'A quieter way to deepen your listening experience, without pressure and at your own pace.'
                  : "Une manière plus calme d'approfondir votre expérience d'écoute, sans pression et à votre rythme."
                }
              </p>

              <div className="inline-flex items-baseline gap-1 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg">
                <span className="text-white/45 text-xs">
                  {language === 'en' ? 'From' : 'À partir de'}
                </span>
                <span className="text-white font-semibold text-base">
                  {language === 'en' ? PREMIUM_PRICING.monthly.priceEn : PREMIUM_PRICING.monthly.price}
                </span>
                <span className="text-white/45 text-xs">
                  {language === 'en' ? PREMIUM_PRICING.monthly.periodEn : PREMIUM_PRICING.monthly.period}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handlePremium}
            className="w-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium py-3 px-6 rounded-xl transition-all shadow-[0_0_24px_rgba(120,110,255,0.12)] flex items-center justify-center gap-2"
          >
            <span>{buttonText || defaultButtonText}</span>
          </button>
        </div>

        <div className="mb-8">
          <LaunchOfferBadge
            isEnglish={language === 'en'}
            variant="default"
          />
        </div>

        <div className="space-y-4">
          <h2 className="text-base font-medium text-white/85 mb-4">
            {language === 'en' ? 'What becomes possible' : 'Ce qui devient possible'}
          </h2>

          {displayFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-white/5 border border-white/10 rounded-2xl p-4 transition-colors hover:bg-white/[0.07]"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-white/65" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-white/90 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-white/55 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={handlePremium}
            className="inline-flex items-center gap-2 text-white/65 hover:text-white font-medium text-sm transition-colors"
          >
            {language === 'en' ? 'Continue with Hearen Premium' : 'Continuer avec Hearen Premium'}
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpsellScreen;